/**
 * The catalogs, each language its own chunk loaded on demand, and what the Spanish one needs before i18next reads
 * it. Kept apart from `index.ts` so a test can load a catalog the way the app does without starting i18next.
 */
import type { Catalog } from "./locales/en";

export type CatalogLanguage = "en" | "es";

const imports: Record<CatalogLanguage, () => Promise<Catalog>> = {
  en: () => import("./locales/en").then((m) => m.en),
  es: () => import("./locales/es").then((m) => m.es),
};

/**
 * Spanish counts in three plural forms, not two: es-419's `many` is the exact millions (1,000,000 and 2,000,000;
 * 1,500,000 is `other`). With no fallback language, i18next answers a count in a form the catalog lacks with the
 * bare key, so each `_other` with no `_many` beside it lends `_many` its text. A catalog that writes its own `_many`
 * (where "1.000.000 de visitas" reads better) keeps it.
 */
export function withManyFromOther<T>(catalog: T): T {
  if (Array.isArray(catalog)) return catalog.map((item: unknown) => withManyFromOther(item)) as T;
  if (catalog === null || typeof catalog !== "object") return catalog;
  const source = catalog as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) out[key] = withManyFromOther(value);
  for (const [key, value] of Object.entries(source)) {
    if (!key.endsWith("_other") || typeof value !== "string") continue;
    const many = `${key.slice(0, -"_other".length)}_many`;
    if (!(many in source)) out[many] = value;
  }
  return out as T;
}

/** One language's catalog, as i18next is given it. */
export function loadCatalog(language: CatalogLanguage): Promise<Catalog> {
  return imports[language]().then((catalog) => (language === "es" ? withManyFromOther(catalog) : catalog));
}

/**
 * The catalog sections that load after the first paint (`locales/part.ts`): the clip editor's, the editor
 * view's, the change ledger's, the Master AI's, the studio's pages (its sidebar labels and error sentences stay in the core) and
 * the generation cards the shell never draws. About two thirds of each language. Each is its own chunk.
 */
type Loader = () => Promise<unknown>;
const PARTS: Array<{ path: readonly string[]; en: Loader; es: Loader }> = [
  { path: ["clipEditor"], en: () => import("./locales/clip-editor.en").then((m) => m.clipEditorEn), es: () => import("./locales/clip-editor.es").then((m) => m.clipEditorEs) },
  { path: ["editorView"], en: () => import("./locales/editor-view.en").then((m) => m.editorViewEn), es: () => import("./locales/editor-view.es").then((m) => m.editorViewEs) },
  { path: ["ledger"], en: () => import("./locales/ledger.en").then((m) => m.ledgerEn), es: () => import("./locales/ledger.es").then((m) => m.ledgerEs) },
  { path: ["master"], en: () => import("./locales/master.en").then((m) => m.masterEn), es: () => import("./locales/master.es").then((m) => m.masterEs) },
  { path: ["settingsHub"], en: () => import("./locales/settings-hub.en").then((m) => m.settingsHubEn), es: () => import("./locales/settings-hub.es").then((m) => m.settingsHubEs) },
  { path: ["spendCosts"], en: () => import("./locales/spend-costs.en").then((m) => m.spendCostsEn), es: () => import("./locales/spend-costs.es").then((m) => m.spendCostsEs) },
  { path: ["studio", "sessions"], en: () => import("./locales/studio/sessions.en").then((m) => m.sessionsEn), es: () => import("./locales/studio/sessions.es").then((m) => m.sessionsEs) },
  { path: ["studio", "home"], en: () => import("./locales/studio/home.en").then((m) => m.homeEn), es: () => import("./locales/studio/home.es").then((m) => m.homeEs) },
  { path: ["studio", "transcript"], en: () => import("./locales/studio/transcript.en").then((m) => m.transcriptEn), es: () => import("./locales/studio/transcript.es").then((m) => m.transcriptEs) },
  { path: ["studio", "composer"], en: () => import("./locales/studio/composer.en").then((m) => m.composerEn), es: () => import("./locales/studio/composer.es").then((m) => m.composerEs) },
  { path: ["studio", "voice"], en: () => import("./locales/studio/voice.en").then((m) => m.voiceEn), es: () => import("./locales/studio/voice.es").then((m) => m.voiceEs) },
  { path: ["studio", "genui"], en: () => import("./locales/studio/genui.en").then((m) => m.genuiEn), es: () => import("./locales/studio/genui.es").then((m) => m.genuiEs) },
  { path: ["studio", "briefForm"], en: () => import("./locales/studio/brief-form.en").then((m) => m.briefFormEn), es: () => import("./locales/studio/brief-form.es").then((m) => m.briefFormEs) },
  { path: ["studio", "jobs"], en: () => import("./locales/studio/jobs.en").then((m) => m.jobsEn), es: () => import("./locales/studio/jobs.es").then((m) => m.jobsEs) },
  { path: ["studio", "jobDetail"], en: () => import("./locales/studio/job-detail.en").then((m) => m.jobDetailEn), es: () => import("./locales/studio/job-detail.es").then((m) => m.jobDetailEs) },
  { path: ["studio", "publish"], en: () => import("./locales/studio/publish.en").then((m) => m.publishEn), es: () => import("./locales/studio/publish.es").then((m) => m.publishEs) },
  { path: ["studio", "admin"], en: () => import("./locales/studio/admin.en").then((m) => m.adminEn), es: () => import("./locales/studio/admin.es").then((m) => m.adminEs) },
  { path: ["studio", "workspace"], en: () => import("./locales/studio/workspace.en").then((m) => m.workspaceEn), es: () => import("./locales/studio/workspace.es").then((m) => m.workspaceEs) },
  { path: ["generation", "voiceover"], en: () => import("./locales/generation/voiceover.en").then((m) => m.voiceoverEn), es: () => import("./locales/generation/voiceover.es").then((m) => m.voiceoverEs) },
  { path: ["generation", "frames"], en: () => import("./locales/generation/frames.en").then((m) => m.framesEn), es: () => import("./locales/generation/frames.es").then((m) => m.framesEs) },
  { path: ["generation", "deckAudio"], en: () => import("./locales/generation/deck-audio.en").then((m) => m.deckAudioEn), es: () => import("./locales/generation/deck-audio.es").then((m) => m.deckAudioEs) },
  { path: ["generation", "revoice"], en: () => import("./locales/generation/revoice.en").then((m) => m.revoiceEn), es: () => import("./locales/generation/revoice.es").then((m) => m.revoiceEs) },
];

/** Where each deferred section goes in the catalog. */
export const CATALOG_PART_PATHS: ReadonlyArray<readonly string[]> = PARTS.map((part) => part.path);

/** The deferred sections of one language, each as the nested object it fills (`{ studio: { jobs: … } }`). */
export function loadCatalogParts(language: CatalogLanguage): Promise<Array<Record<string, unknown>>> {
  return Promise.all(
    PARTS.map(async ({ path, [language]: load }) => {
      let node = (await load()) as unknown;
      if (language === "es") node = withManyFromOther(node);
      for (const key of [...path].reverse()) node = { [key]: node };
      return node as Record<string, unknown>;
    }),
  );
}

/** Deep merge of plain objects, for a catalog and its sections (tests; i18next merges its own). */
function merge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...target };
  for (const [key, value] of Object.entries(source)) {
    const current = out[key];
    out[key] =
      current && value && typeof current === "object" && typeof value === "object" && !Array.isArray(value)
        ? merge(current as Record<string, unknown>, value as Record<string, unknown>)
        : value;
  }
  return out;
}

/** One language whole: the core and every deferred section, as the app holds it once all are in. */
export async function loadFullCatalog(language: CatalogLanguage): Promise<Catalog> {
  const [core, parts] = await Promise.all([loadCatalog(language), loadCatalogParts(language)]);
  return parts.reduce<Record<string, unknown>>((all, part) => merge(all, part), core as unknown as Record<string, unknown>) as unknown as Catalog;
}
